// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { InfoOutlined } from '@/constants/hardfin-standard-icons';
import { INVOICE_MODE } from '@/constants/invoice-mode';
import { IsoCurrencyCode } from '@/currency/constants/iso';
import { formatCurrencyValue } from '@/currency/utilities/format';
import {
  BILLING_PLAN_INPUT_WIDTH,
  BillingPlanFormValues,
  CURRENCY_INPUT_SX,
  EXACT_INVOICE_FREQUENCIES,
  FREQUENCY_OPTIONS,
  TOGGLE_BUTTON_GROUP_SX,
  TOGGLE_BUTTON_SX,
} from '@/project/components/billing-plan-constants';
import {
  AmountRange,
  formatAmountRange,
} from '@/project/utilities/charge-amounts';
import {
  Alert,
  Box,
  FormControl,
  MenuItem,
  Paper,
  Select,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { Decimal } from 'decimal.js';
import { Control, Controller } from 'react-hook-form';

interface Props {
  control: Control<BillingPlanFormValues>;
  hasDates: boolean;
  // When true, the section renders a milestone-mode placeholder explaining
  // that the invoice schedule is determined by milestone achievement
  // instead of the toggle / frequency inputs. The card is still rendered
  // so the drawer's structure stays consistent across schedule types.
  isMilestone: boolean;
  isFrequencyPairValid: boolean;
  chargeFrequency: string | null;
  invoiceFrequency: string | null;
  lineItemTotal: Decimal;
  numInvoices: number;
  estInvoiceRange: AmountRange | null;
  currencyCode: IsoCurrencyCode;
}

export function BillingPlanInvoiceScheduleSection(props: Props) {
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
        Invoice schedule
      </Typography>
      {props.isMilestone ? (
        <Typography variant="body2" color="text.secondary">
          Invoice schedule is determined by milestone achievement
        </Typography>
      ) : !props.hasDates ? (
        <Typography variant="caption" color="text.secondary">
          Please configure a start and end date on the line item to configure an
          invoice schedule
        </Typography>
      ) : (
        <>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
              }}
            >
              <Controller
                control={props.control}
                name="invoiceMode"
                render={(renderProps) => (
                  <ToggleButtonGroup
                    exclusive
                    size="small"
                    sx={TOGGLE_BUTTON_GROUP_SX}
                    value={renderProps.field.value}
                    onChange={(_event, value) => {
                      if (value !== null) {
                        renderProps.field.onChange(value);
                      }
                    }}
                  >
                    <ToggleButton
                      value={INVOICE_MODE.ADVANCE}
                      sx={TOGGLE_BUTTON_SX}
                    >
                      In advance
                    </ToggleButton>
                    <ToggleButton
                      value={INVOICE_MODE.ARREARS}
                      sx={TOGGLE_BUTTON_SX}
                    >
                      In arrears
                    </ToggleButton>
                  </ToggleButtonGroup>
                )}
              />
              <Tooltip
                arrow
                title={
                  <Box>
                    <Typography variant="caption" fontWeight={600}>
                      In advance:
                    </Typography>
                    <Typography variant="caption">
                      {' '}
                      bill at the start of each billing period (pre-bill).
                    </Typography>
                    <Box sx={{ mt: 1 }} />
                    <Typography variant="caption" fontWeight={600}>
                      In arrears:
                    </Typography>
                    <Typography variant="caption">
                      {' '}
                      bill at the end of each billing period (post-bill).
                    </Typography>
                  </Box>
                }
              >
                <InfoOutlined
                  sx={{
                    fontSize: '1rem',
                    color: 'text.secondary',
                    cursor: 'help',
                  }}
                />
              </Tooltip>
            </Box>
            <Controller
              control={props.control}
              name="invoiceFrequency"
              render={(renderProps) => (
                <FormControl
                  size="small"
                  sx={{ ...CURRENCY_INPUT_SX, width: BILLING_PLAN_INPUT_WIDTH }}
                >
                  <Select
                    displayEmpty
                    value={renderProps.field.value ?? ''}
                    onChange={(event) => {
                      renderProps.field.onChange(event.target.value || null);
                    }}
                    disabled={props.chargeFrequency === 'ONCE'}
                  >
                    {FREQUENCY_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />
          </Box>
          <Box sx={{ mt: 1, pl: 1 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'baseline',
              }}
            >
              <Typography variant="caption" color="text.secondary">
                {formatCurrencyValue({
                  amount: props.lineItemTotal,
                  isoCode: props.currencyCode,
                }) + ' total'}
                {' \u00F7 '}
                {props.invoiceFrequency && props.numInvoices > 0
                  ? `${props.numInvoices} invoices`
                  : 'Not set'}
              </Typography>
              <Typography variant="caption" sx={{ pr: 1 }}>
                Est. invoice{' '}
                <Box component="span" fontWeight={700}>
                  {props.numInvoices > 0 && props.estInvoiceRange !== null
                    ? (props.chargeFrequency !== props.invoiceFrequency &&
                      !EXACT_INVOICE_FREQUENCIES.has(
                        props.invoiceFrequency ?? ''
                      )
                        ? '~'
                        : '') +
                      formatAmountRange(
                        props.estInvoiceRange,
                        props.currencyCode
                      )
                    : '\u2014'}
                </Box>
              </Typography>
            </Box>
          </Box>

          {!props.isFrequencyPairValid && (
            <Alert severity="error" sx={{ mt: 1.5, py: 0.5 }}>
              <Typography variant="body2">
                Invoice schedule cannot be more frequent than the charge
                schedule
              </Typography>
            </Alert>
          )}
        </>
      )}
    </Paper>
  );
}
